import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { RoadmapChild, RoadmapEpic, RoadmapResponse, RoadmapSprint } from "./useRoadmap";

const STATUS_COLORS: Record<string, string> = {
    new: "#7F8C8D",
    indeterminate: "#2D9CDB",
    done: "#1C7A5A",
};

const ACTIVE_SPRINT_COLOR = "#9B59B6";
const LABEL_COL = "280px";
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const parseDay = (s: string | null): Date | null => {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};

const addDays = (d: Date, days: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
const addMonths = (d: Date, months: number) => new Date(d.getFullYear(), d.getMonth() + months, 1);
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

const fmtDay = (s: string | null) => {
    const d = parseDay(s);
    return d ? `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : "—";
};

const agoLabel = (iso?: string) => {
    if (!iso) return "";
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (mins < 1) return "Updated just now";
    if (mins < 60) return `Updated ${mins}m ago`;
    return `Updated ${Math.round(mins / 60)}h ago`;
};

type Issue = RoadmapEpic | RoadmapChild;

// Bar geometry as percentages of the plot width. `milestone` marks a
// single-point diamond (date mode only); `fade` an open-ended bar.
interface Geom {
    left: number;
    width: number;
    fade?: boolean;
    milestone?: boolean;
}

function Panel({ children }: { children: React.ReactNode }) {
    return (
        <div className="rounded-[14px] border border-[#E8E4DE] bg-white px-6 py-10 text-center text-[13px] text-[#7A7268]">
            {children}
        </div>
    );
}

function KeyChip({ issue, small }: { issue: Issue; small?: boolean }) {
    return (
        <span
            className={`font-mono font-semibold rounded bg-[#F4F2EE] text-[#7A7268] px-1.5 py-px flex-shrink-0 ${
                small ? "text-[10px]" : "text-[11px]"
            }`}
        >
            {issue.key}
        </span>
    );
}

export function RoadmapTimeline({
    data,
    loading,
    error,
    onRefresh,
}: {
    data: RoadmapResponse | null;
    loading: boolean;
    error: unknown;
    onRefresh: () => void;
}) {
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [tip, setTip] = useState<{ rect: DOMRect; issue: Issue } | null>(null);

    const epics = data?.epics ?? [];
    const sprints = data?.sprints ?? [];
    // Sprint columns are the primary axis (how the TDC board plans); the
    // date-based axis is the fallback for projects that schedule with
    // start/due dates instead.
    const mode: "sprint" | "date" = sprints.length > 0 ? "sprint" : "date";

    const today = useMemo(() => new Date(), []);

    // ---- sprint-mode geometry -------------------------------------------
    const sprintIdxById = useMemo(() => {
        const m = new Map<number, number>();
        sprints.forEach((s, i) => m.set(s.id, i));
        return m;
    }, [sprints]);

    const sprintSpan = (issue: Issue): [number, number] | null => {
        let min = Infinity;
        let max = -Infinity;
        issue.sprints.forEach((s) => {
            const idx = sprintIdxById.get(s.id);
            if (idx === undefined) return;
            if (idx < min) min = idx;
            if (idx > max) max = idx;
        });
        return min === Infinity ? null : [min, max];
    };

    const epicSpan = (epic: RoadmapEpic): [number, number] | null => {
        let min = Infinity;
        let max = -Infinity;
        const fold = (s: [number, number] | null) => {
            if (!s) return;
            min = Math.min(min, s[0]);
            max = Math.max(max, s[1]);
        };
        fold(sprintSpan(epic));
        epic.children.forEach((c) => fold(sprintSpan(c)));
        return min === Infinity ? null : [min, max];
    };

    // Position of "now": proportional progress through the active sprint.
    const nowPct = useMemo(() => {
        if (mode !== "sprint") return null;
        const activeIdx = sprints.findIndex((s) => s.state === "active");
        if (activeIdx === -1) return null;
        const s = sprints[activeIdx];
        const start = parseDay(s.start);
        const end = parseDay(s.end);
        let frac = 0.5;
        if (start && end && end > start) {
            frac = Math.min(1, Math.max(0, (today.getTime() - start.getTime()) / (end.getTime() - start.getTime())));
        }
        return ((activeIdx + frac) / sprints.length) * 100;
    }, [mode, sprints, today]);

    // ---- date-mode geometry ---------------------------------------------
    const { domainStart, domainEnd, months } = useMemo(() => {
        const dates: Date[] = [];
        epics.forEach((e) => {
            [e.start, e.due].forEach((s) => {
                const d = parseDay(s);
                if (d) dates.push(d);
            });
            e.children.forEach((c) =>
                [c.start, c.due].forEach((s) => {
                    const d = parseDay(s);
                    if (d) dates.push(d);
                }),
            );
        });
        let min = addMonths(today, -1);
        let max = addMonths(today, 2);
        dates.forEach((d) => {
            if (d < min) min = d;
            if (d > max) max = d;
        });
        const start = startOfMonth(min);
        const end = addMonths(max, 1);
        const list: Date[] = [];
        for (let m = start; m < end; m = addMonths(m, 1)) list.push(m);
        return { domainStart: start, domainEnd: end, months: list };
    }, [epics, today]);

    const span = domainEnd.getTime() - domainStart.getTime();
    const datePct = (d: Date) => Math.min(100, Math.max(0, ((d.getTime() - domainStart.getTime()) / span) * 100));
    const denseAxis = months.length > 9;
    const todayPct = mode === "sprint" ? nowPct : datePct(today);

    const geomFor = (issue: Issue, isEpic: boolean): Geom | null => {
        if (mode === "sprint") {
            const s = isEpic ? epicSpan(issue as RoadmapEpic) : sprintSpan(issue);
            if (!s) return null;
            const n = sprints.length;
            return { left: (s[0] / n) * 100, width: ((s[1] - s[0] + 1) / n) * 100 };
        }
        let start = parseDay(issue.start);
        let due = parseDay(issue.due);
        if (start && due && start > due) [start, due] = [due, start];
        if (start && !due) {
            let end = addDays(today, 14);
            if (end <= start) end = addDays(start, 7);
            return { left: datePct(start), width: Math.max(datePct(end) - datePct(start), 0.5), fade: true };
        }
        if (start && due) {
            return { left: datePct(start), width: Math.max(datePct(due) - datePct(start), 0.5) };
        }
        if (due) return { left: datePct(due), width: 0, milestone: true };
        return null;
    };

    // ---- rows -----------------------------------------------------------
    const scheduled = useMemo(() => {
        const rows = epics.filter((e) => geomFor(e, true) !== null);
        return [...rows].sort((a, b) => {
            const doneA = a.statusCategory === "done" ? 1 : 0;
            const doneB = b.statusCategory === "done" ? 1 : 0;
            if (doneA !== doneB) return doneA - doneB;
            const gA = geomFor(a, true);
            const gB = geomFor(b, true);
            return (gA ? gA.left : 100) - (gB ? gB.left : 100);
        });
        // eslint-disable-next-line
    }, [epics, mode, sprints, domainStart, domainEnd]);
    const unscheduled = useMemo(
        () => epics.filter((e) => geomFor(e, true) === null),
        // eslint-disable-next-line
        [epics, mode, sprints, domainStart, domainEnd],
    );

    const showTip = (issue: Issue) => (e: React.MouseEvent<HTMLElement>) =>
        setTip({ rect: e.currentTarget.getBoundingClientRect(), issue });
    const hideTip = () => setTip(null);

    const sprintRangeLabel = (issue: Issue, isEpic: boolean): string | null => {
        const s = isEpic ? epicSpan(issue as RoadmapEpic) : sprintSpan(issue);
        if (!s) return null;
        const first = sprints[s[0]];
        const last = sprints[s[1]];
        return s[0] === s[1] ? first.name : `${first.name} → ${last.name}`;
    };

    const renderBar = (issue: Issue, isEpic: boolean) => {
        const color = STATUS_COLORS[issue.statusCategory] || STATUS_COLORS.new;
        const geom = geomFor(issue, isEpic);
        if (!geom) return null;
        if (geom.milestone) {
            return (
                <div
                    className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rotate-45 cursor-default ${
                        isEpic ? "w-2.5 h-2.5" : "w-2 h-2"
                    }`}
                    style={{ left: `${geom.left}%`, background: color }}
                    onMouseEnter={showTip(issue)}
                    onMouseLeave={hideTip}
                />
            );
        }
        return (
            <div
                className={`absolute top-1/2 -translate-y-1/2 rounded-[5px] cursor-default overflow-hidden ${
                    isEpic ? "h-[18px]" : "h-2.5"
                }`}
                style={{
                    left: `calc(${geom.left}% + ${mode === "sprint" ? "3px" : "0px"})`,
                    width: `max(calc(${geom.width}% - ${mode === "sprint" ? "6px" : "0px"}), 8px)`,
                    background: geom.fade ? `linear-gradient(90deg, ${color} 55%, ${color}22 100%)` : color,
                    border: geom.fade ? `1px dashed ${color}` : "none",
                }}
                onMouseEnter={showTip(issue)}
                onMouseLeave={hideTip}
            >
                {isEpic && geom.width > 12 && (
                    <span className="text-[10px] font-semibold text-white px-2 leading-[18px] whitespace-nowrap">
                        {issue.status}
                    </span>
                )}
            </div>
        );
    };

    // ---- states ---------------------------------------------------------
    if (loading && !data) {
        return (
            <div className="rounded-[14px] border border-[#E8E4DE] bg-white p-5">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-4 py-2.5 animate-pulse">
                        <div className="h-4 rounded bg-[#F0EDE8]" style={{ width: LABEL_COL }} />
                        <div className="h-4 flex-1 rounded bg-[#F7F5F1]" />
                    </div>
                ))}
            </div>
        );
    }
    if (error && !data) {
        return (
            <Panel>
                The roadmap could not be loaded from Jira.
                <button
                    type="button"
                    onClick={onRefresh}
                    className="ml-3 text-[13px] font-semibold text-[#9B59B6] cursor-pointer bg-transparent border-none hover:underline"
                >
                    Retry
                </button>
            </Panel>
        );
    }
    if (data && !data.configured) {
        return <Panel>The Jira integration is not configured yet. Set the DL_JIRA_* environment variables on the API to enable the roadmap.</Panel>;
    }
    if (!data || epics.length === 0) {
        return <Panel>No epics match the roadmap filter in {data?.projectKey || "Jira"}.</Panel>;
    }

    const backlogLabel = mode === "sprint" ? "No sprint" : "No dates";

    return (
        <div className="rounded-[14px] border border-[#E8E4DE] bg-white">
            <div className="flex items-center justify-between px-5 pt-4 pb-1">
                <span className="text-[12px] text-[#B5AFA5]">{agoLabel(data.fetchedAt)}</span>
                <button
                    type="button"
                    onClick={onRefresh}
                    disabled={loading}
                    className="text-[12.5px] font-semibold text-[#9B59B6] cursor-pointer bg-transparent border-none hover:underline disabled:opacity-50"
                >
                    {loading ? "Refreshing…" : "↻ Refresh"}
                </button>
            </div>

            <div className="overflow-x-auto">
                <div className="min-w-[900px] px-5 pb-5">
                    {/* Axis */}
                    <div className="grid" style={{ gridTemplateColumns: `${LABEL_COL} 1fr` }}>
                        <div />
                        <div className="relative h-8">
                            {mode === "sprint"
                                ? sprints.map((s, i) => {
                                      const isActive = s.state === "active";
                                      return (
                                          <div
                                              key={s.id}
                                              className="absolute bottom-0 top-0 flex items-end justify-center overflow-hidden px-1"
                                              style={{
                                                  left: `${(i / sprints.length) * 100}%`,
                                                  width: `${(1 / sprints.length) * 100}%`,
                                              }}
                                          >
                                              <span
                                                  className={`text-[10.5px] font-semibold uppercase tracking-[0.5px] truncate pb-1 ${
                                                      isActive ? "" : "text-[#B5AFA5]"
                                                  }`}
                                                  style={isActive ? { color: ACTIVE_SPRINT_COLOR } : undefined}
                                                  title={`${s.name}${s.start ? ` · ${fmtDay(s.start)} → ${fmtDay(s.end)}` : ""}`}
                                              >
                                                  {s.name}
                                                  {isActive ? " ●" : ""}
                                              </span>
                                          </div>
                                      );
                                  })
                                : months.map((m, i) => {
                                      const isQuarter = m.getMonth() % 3 === 0;
                                      if (denseAxis && !isQuarter) return null;
                                      const label = denseAxis
                                          ? `Q${m.getMonth() / 3 + 1} ${m.getFullYear()}`
                                          : `${MONTH_NAMES[m.getMonth()]}${m.getMonth() === 0 || i === 0 ? ` ${m.getFullYear()}` : ""}`;
                                      return (
                                          <span
                                              key={m.toISOString()}
                                              className="absolute bottom-1 pl-1.5 text-[10.5px] font-semibold uppercase tracking-[0.5px] text-[#B5AFA5] whitespace-nowrap"
                                              style={{ left: `${datePct(m)}%` }}
                                          >
                                              {label}
                                          </span>
                                      );
                                  })}
                        </div>
                    </div>

                    {/* Body with gridline/now overlay confined to the plot column */}
                    <div className="relative">
                        <div className="absolute inset-y-0 right-0 pointer-events-none" style={{ left: LABEL_COL }}>
                            {mode === "sprint" ? (
                                <>
                                    {sprints.map((s, i) => {
                                        const isActive = s.state === "active";
                                        return (
                                            <React.Fragment key={s.id}>
                                                {i > 0 && (
                                                    <div
                                                        className="absolute inset-y-0 w-px bg-[#F0EDE8]"
                                                        style={{ left: `${(i / sprints.length) * 100}%` }}
                                                    />
                                                )}
                                                {isActive && (
                                                    <div
                                                        className="absolute inset-y-0"
                                                        style={{
                                                            left: `${(i / sprints.length) * 100}%`,
                                                            width: `${(1 / sprints.length) * 100}%`,
                                                            background: `${ACTIVE_SPRINT_COLOR}0D`,
                                                        }}
                                                    />
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </>
                            ) : (
                                months.map((m) => (
                                    <div
                                        key={m.toISOString()}
                                        className="absolute inset-y-0 w-px bg-[#F0EDE8]"
                                        style={{ left: `${datePct(m)}%` }}
                                    />
                                ))
                            )}
                            {todayPct !== null && todayPct > 0 && todayPct < 100 && (
                                <div
                                    className="absolute inset-y-0 w-px border-l border-dashed border-[#D4546A]"
                                    style={{ left: `${todayPct}%` }}
                                />
                            )}
                        </div>

                        {scheduled.map((epic) => {
                            const isOpen = expanded.has(epic.key);
                            const plottedChildren = epic.children.filter((c) => geomFor(c, false) !== null);
                            const backlogChildren = epic.children.filter((c) => geomFor(c, false) === null);
                            return (
                                <React.Fragment key={epic.key}>
                                    <div
                                        className="grid items-center border-t border-[#F4F2EE]"
                                        style={{ gridTemplateColumns: `${LABEL_COL} 1fr`, height: 40 }}
                                    >
                                        <div className="flex items-center gap-1.5 pr-3 min-w-0">
                                            <button
                                                type="button"
                                                aria-label={isOpen ? "Collapse children" : "Expand children"}
                                                disabled={epic.children.length === 0}
                                                onClick={() =>
                                                    setExpanded((prev) => {
                                                        const next = new Set(prev);
                                                        if (next.has(epic.key)) next.delete(epic.key);
                                                        else next.add(epic.key);
                                                        return next;
                                                    })
                                                }
                                                className={`w-5 h-5 flex items-center justify-center bg-transparent border-none text-[#9B958C] flex-shrink-0 ${
                                                    epic.children.length === 0 ? "opacity-0" : "cursor-pointer hover:text-[#1C1A17]"
                                                }`}
                                            >
                                                <span className={`text-[10px] transition-transform ${isOpen ? "rotate-90" : ""}`}>▶</span>
                                            </button>
                                            <KeyChip issue={epic} />
                                            <a
                                                href={epic.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-[13px] font-medium text-[#1C1A17] no-underline hover:text-[#9B59B6] truncate"
                                                onMouseEnter={showTip(epic)}
                                                onMouseLeave={hideTip}
                                            >
                                                {epic.summary}
                                            </a>
                                            {epic.childStats.total > 0 && (
                                                <span className="text-[10.5px] font-semibold text-[#9B958C] flex-shrink-0">
                                                    {epic.childStats.done}/{epic.childStats.total}
                                                </span>
                                            )}
                                        </div>
                                        <div className="relative h-full">{renderBar(epic, true)}</div>
                                    </div>

                                    {isOpen &&
                                        plottedChildren.map((child) => (
                                            <div
                                                key={child.key}
                                                className="grid items-center border-t border-[#F8F6F3] bg-[#FCFBF9]"
                                                style={{ gridTemplateColumns: `${LABEL_COL} 1fr`, height: 30 }}
                                            >
                                                <div className="flex items-center gap-1.5 pl-8 pr-3 min-w-0">
                                                    <KeyChip issue={child} small />
                                                    <a
                                                        href={child.url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-[12px] text-[#5C574F] no-underline hover:text-[#9B59B6] truncate"
                                                        onMouseEnter={showTip(child)}
                                                        onMouseLeave={hideTip}
                                                    >
                                                        {child.summary}
                                                    </a>
                                                </div>
                                                <div className="relative h-full">{renderBar(child, false)}</div>
                                            </div>
                                        ))}
                                    {isOpen && backlogChildren.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-1.5 border-t border-[#F8F6F3] bg-[#FCFBF9] py-1.5 pl-8 pr-3">
                                            <span className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-[#B5AFA5] mr-1">
                                                {backlogLabel}
                                            </span>
                                            {backlogChildren.map((child) => (
                                                <a
                                                    key={child.key}
                                                    href={child.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="flex items-center gap-1 text-[11px] text-[#5C574F] no-underline bg-white border border-[#EDEAE5] rounded-md px-1.5 py-0.5 hover:border-[#9B59B6]"
                                                    onMouseEnter={showTip(child)}
                                                    onMouseLeave={hideTip}
                                                >
                                                    <span
                                                        className="w-1.5 h-1.5 rounded-full"
                                                        style={{ background: STATUS_COLORS[child.statusCategory] || STATUS_COLORS.new }}
                                                    />
                                                    {child.key}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    {unscheduled.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-[#F4F2EE]">
                            <div className="text-[10.5px] font-semibold uppercase tracking-[0.5px] text-[#B5AFA5] mb-1.5">
                                {mode === "sprint" ? "Backlog (no sprint)" : "Unscheduled"}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {unscheduled.map((epic) => (
                                    <a
                                        key={epic.key}
                                        href={epic.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-1.5 text-[12px] text-[#5C574F] no-underline bg-[#FAFAF8] border border-[#EDEAE5] rounded-md px-2 py-1 hover:border-[#9B59B6] max-w-[320px]"
                                        onMouseEnter={showTip(epic)}
                                        onMouseLeave={hideTip}
                                    >
                                        <span
                                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                            style={{ background: STATUS_COLORS[epic.statusCategory] || STATUS_COLORS.new }}
                                        />
                                        <span className="font-mono font-semibold text-[10.5px] text-[#7A7268] flex-shrink-0">{epic.key}</span>
                                        <span className="truncate">{epic.summary}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {tip &&
                createPortal(
                    <div
                        className="fixed z-[9999] pointer-events-none"
                        style={{
                            left: Math.min(Math.max(tip.rect.left + tip.rect.width / 2, 140), window.innerWidth - 140),
                            top: tip.rect.top - 8,
                            transform: "translate(-50%, -100%)",
                        }}
                    >
                        <div className="bg-white border border-[#E8E4DE] rounded-[12px] px-3.5 py-2.5 shadow-[0_8px_24px_rgba(0,0,0,0.12)] max-w-[280px]">
                            <div className="flex items-center gap-1.5">
                                <span className="font-mono text-[10.5px] font-semibold text-[#7A7268]">{tip.issue.key}</span>
                                <span
                                    className="text-[10px] font-semibold px-1.5 py-px rounded text-white"
                                    style={{ background: STATUS_COLORS[tip.issue.statusCategory] || STATUS_COLORS.new }}
                                >
                                    {tip.issue.status}
                                </span>
                            </div>
                            <div className="text-[12.5px] font-semibold text-[#1C1A17] mt-1">{tip.issue.summary}</div>
                            {mode === "sprint" ? (
                                <div className="text-[11px] text-[#7A7268] mt-1">
                                    {sprintRangeLabel(tip.issue, "childStats" in tip.issue) || backlogLabel}
                                </div>
                            ) : (
                                <div className="text-[11px] text-[#7A7268] mt-1">
                                    {fmtDay(tip.issue.start)} → {fmtDay(tip.issue.due)}
                                </div>
                            )}
                            {tip.issue.assignee && (
                                <div className="text-[11px] text-[#7A7268] mt-0.5">{tip.issue.assignee.name}</div>
                            )}
                            {"childStats" in tip.issue && tip.issue.childStats.total > 0 && (
                                <div className="text-[11px] text-[#7A7268] mt-0.5">
                                    {tip.issue.childStats.done} of {tip.issue.childStats.total} child issues done
                                </div>
                            )}
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
}
